/**
 * BulkCreateOperationToast - Persistent toast showing bulk create operation progress
 *
 * Features:
 * - Live progress bar with polling
 * - Phase indicator
 * - Success/skip/error counts
 * - View details and dismiss buttons
 */

import { CheckCircle, Eye, Loader2, XCircle } from 'lucide-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/catalyst/button';
import { cn } from '@/lib/utils';
import {
  enhancedApi as externalTaskApi,
  type ExternalTaskBulkCreateOperationSchema,
} from '@/services/api/generated/externalTaskService';

interface BulkCreateOperationToastProps {
  /** Operation ID to track */
  operationId: string;
  /** Callback when operation completes */
  onComplete?: (operation: ExternalTaskBulkCreateOperationSchema) => void;
  /** Callback when user dismisses the toast */
  onDismiss?: () => void;
}

const PHASE_LABELS: Record<string, string> = {
  VALIDATING: 'Validating tasks...',
  CREATING_TASKS: 'Creating tasks...',
  DONE: 'Complete',
};

export const BulkCreateOperationToast: React.FC<
  BulkCreateOperationToastProps
> = ({ operationId, onComplete, onDismiss }) => {
  const navigate = useNavigate();
  const [hasNotifiedComplete, setHasNotifiedComplete] = React.useState(false);

  // Determine if we should poll
  const shouldPoll = React.useRef(true);

  // Fetch operation status with polling
  const { data: operation } =
    externalTaskApi.useExternalTaskBulkCreateRetrieveQuery(
      { id: operationId },
      {
        pollingInterval: shouldPoll.current ? 2000 : undefined,
        skip: !operationId,
        skipPollingIfUnfocused: true,
      },
    );

  // Stop polling when operation is complete
  React.useEffect(() => {
    if (
      operation &&
      (operation.status === 'COMPLETED' ||
        operation.status === 'FAILED' ||
        operation.status === 'CANCELLED')
    ) {
      shouldPoll.current = false;

      // Notify parent of completion (only once)
      if (!hasNotifiedComplete) {
        setHasNotifiedComplete(true);
        onComplete?.(operation);
      }
    }
  }, [operation, onComplete, hasNotifiedComplete]);

  // Handle view details click
  const handleViewDetails = React.useCallback(() => {
    navigate(`/external-tasks/bulk-create-operations/${operationId}`);
  }, [navigate, operationId]);

  // Fixed dimensions to prevent Sonner's height tracking issues
  const TOAST_WIDTH = 380;
  const TOAST_HEIGHT = 140;

  if (!operation) {
    return (
      <div
        className="flex items-center justify-center bg-white dark:bg-zinc-900 rounded-lg shadow-lg border border-zinc-200 dark:border-zinc-700"
        style={{ width: TOAST_WIDTH, height: TOAST_HEIGHT }}
      >
        <div className="flex items-center gap-3">
          <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
          <span className="text-sm text-zinc-600 dark:text-zinc-400">
            Loading operation status...
          </span>
        </div>
      </div>
    );
  }

  const isRunning =
    operation.status === 'PENDING' || operation.status === 'RUNNING';
  const isCompleted = operation.status === 'COMPLETED';
  const isFailed = operation.status === 'FAILED';

  const processedCount =
    operation.success_count + operation.skipped_count + operation.error_count;
  const progressPercent =
    operation.total_count > 0
      ? Math.round((processedCount / operation.total_count) * 100)
      : 0;

  return (
    <div
      className="flex flex-col gap-3 p-4 bg-white dark:bg-zinc-900 rounded-lg shadow-lg border border-zinc-200 dark:border-zinc-700"
      style={{ width: TOAST_WIDTH, height: TOAST_HEIGHT }}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {isRunning && (
            <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
          )}
          {isCompleted && <CheckCircle className="h-5 w-5 text-green-600" />}
          {isFailed && <XCircle className="h-5 w-5 text-red-600" />}
          <span className="font-medium text-zinc-900 dark:text-white">
            {isRunning && 'Creating Internal Tasks'}
            {isCompleted && 'Tasks Created'}
            {isFailed && 'Operation Failed'}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <Button
            plain
            onClick={handleViewDetails}
            className="p-1.5 text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
            title="View details"
          >
            <Eye className="h-4 w-4" />
          </Button>
          <Button
            plain
            onClick={onDismiss}
            className="p-1.5 text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
            title="Dismiss"
          >
            <XCircle className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="space-y-1.5">
        <div className="h-2 bg-zinc-100 dark:bg-zinc-800 rounded-full overflow-hidden">
          <div
            className={cn(
              'h-full transition-all duration-500 ease-out rounded-full',
              isRunning && 'bg-blue-600',
              isCompleted && 'bg-green-600',
              isFailed && 'bg-red-600',
            )}
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-zinc-500 dark:text-zinc-400">
            {PHASE_LABELS[operation.phase] || operation.phase}
          </span>
          <span className="text-zinc-600 dark:text-zinc-300 font-medium">
            {processedCount}/{operation.total_count} tasks
          </span>
        </div>
      </div>

      {/* Status counts */}
      {(operation.success_count > 0 ||
        operation.skipped_count > 0 ||
        operation.error_count > 0) && (
        <div className="flex items-center gap-3 text-xs">
          {operation.success_count > 0 && (
            <span className="text-green-600 dark:text-green-400">
              {operation.success_count} created
            </span>
          )}
          {operation.skipped_count > 0 && (
            <span className="text-amber-600 dark:text-amber-400">
              {operation.skipped_count} skipped
            </span>
          )}
          {operation.error_count > 0 && (
            <span className="text-red-600 dark:text-red-400">
              {operation.error_count} failed
            </span>
          )}
        </div>
      )}

      {/* Error message if failed */}
      {isFailed && operation.error_message && (
        <p className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-2 rounded">
          {operation.error_message}
        </p>
      )}
    </div>
  );
};
